import { ref, watch } from 'vue';

import { useDynamicYieldCampaigns } from '~/composables/use-dynamic-yield-campaigns';
import { CAMPAIGN_STATUS, ENGAGEMENT_TYPES, GLOBAL_CAMPAIGNS } from '~/constants/campaigns';

/**
 * Validate cart layout campaign data
 * @param {Object} data - Raw campaign data
 * @returns {Object} Validated campaign data
 */
const validateCartLayoutData = (data) => {
  if (typeof data !== 'object') {
    return { showOldLayout: false };
  }

  return {
    showOldLayout: typeof data.showOldLayout === 'boolean' ? data.showOldLayout : false,
  };
};

/**
 * Composable for managing cart layout campaign functionality
 * @param {string} section - The section identifier where this campaign is being used
 * @returns {Object} Campaign state and methods
 */
export function useCartLayoutCampaign(section) {
  const { getCampaigns, reportCampaignEngagement, transformCampaignData } =
    useDynamicYieldCampaigns();

  const cartLayoutData = ref(null);
  const showOldLayout = ref(false);
  const campaignStatus = ref(CAMPAIGN_STATUS.IDLE);

  const handleCartLayoutCampaign = (campaign) => {
    if (!campaign?.enabled) {
      showOldLayout.value = false; // Default to new layout
      campaignStatus.value = CAMPAIGN_STATUS.ERROR;
      return;
    }

    try {
      const transformedCampaign = transformCampaignData(campaign, 0);
      const validatedData = validateCartLayoutData(transformedCampaign);

      cartLayoutData.value = validatedData;
      showOldLayout.value = validatedData.showOldLayout;
      campaignStatus.value = CAMPAIGN_STATUS.SUCCESS;

      // Report impression to Dynamic Yield
      reportCampaignEngagement({
        campaignName: campaign.selectorName,
        engagementType: ENGAGEMENT_TYPES.IMPRESSION,
        section,
      });
    } catch (error) {
      console.error('[Cart Layout Campaign] Error processing campaign data:', error);
      showOldLayout.value = false; // Fallback to new layout
      campaignStatus.value = CAMPAIGN_STATUS.ERROR;
    }
  };

  // Watch specific campaign instead of all campaigns for better performance
  watch(
    () => getCampaigns()[GLOBAL_CAMPAIGNS.CART_LAYOUT.selectorName],
    (campaign) => {
      if (!campaign) return;

      campaignStatus.value =
        campaign.status === CAMPAIGN_STATUS.LOADING ? CAMPAIGN_STATUS.LOADING : campaign.status;

      if (campaign.status === CAMPAIGN_STATUS.SUCCESS) {
        handleCartLayoutCampaign(campaign);
      } else if (campaign.status === CAMPAIGN_STATUS.ERROR) {
        showOldLayout.value = false; // Fallback to new layout
        campaignStatus.value = CAMPAIGN_STATUS.ERROR;
      }
    },
    { immediate: true },
  );

  return {
    showOldLayout,
  };
}
